/**
 * BriefTab — War Room Brief
 *
 * A readable one-page brief that replaces the need to read raw agent JSON:
 * headline numbers, where the seats agree, open disputes, factual defects
 * flagged by the consistency gate, a preview of the carrier's likely attack,
 * next actions, and caveats. Backed by GET /deliberation/{reportId}/brief.
 *
 * Every leaf value is nullable and every list may be empty — this component
 * renders defensively and hides a section entirely when its data is empty,
 * relying on `brief.sections_omitted` (server-authored) as the fine-print
 * explanation rather than re-deriving why a section is missing.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { safeText } from '@/lib/safeText';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { CarrierPositionLedger } from '@/components/war-room/CarrierPositionLedger';
import { formatCurrency } from '@/utils/financialFormat';
import {
  FileText,
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Swords,
  Scale,
  Info,
  ListChecks,
  ShieldAlert,
  Layers,
  Send,
} from 'lucide-react';
import {
  type Brief,
  type BriefGateFinding,
  type BriefCounterStrategy,
  type BriefCollectionStatus,
  type BriefDocumentRef,
  type BriefMethodologyPath,
  type CarrierEmphasis,
  getAgentDisplayName,
  getBrief,
  downloadBriefMarkdown,
  requestBriefDocuments,
} from '@/services/deliberationApi';
import type { LadderAttest } from '@/components/war-room/CarrierPositionLedger';
import { getInterpretation, getInterpretationCandidates } from '@/services/interpretationApi';
import { useWorkspaceRole } from '@/hooks/useWorkspaceRole';

// =============================================================================
// PROPS
// =============================================================================

interface BriefTabProps {
  reportId: string;
}

// =============================================================================
// HELPERS
// =============================================================================

const severityChipStyle: Record<string, React.CSSProperties> = {
  blocker: {
    backgroundColor: 'var(--color-status-adverse)',
    color: 'var(--color-on-brand)',
    borderColor: 'var(--color-status-adverse)',
  },
  warning: {
    backgroundColor: 'var(--color-needs-human-tint)',
    color: 'var(--color-needs-human-text)',
    borderColor: 'var(--color-needs-human-tint-bd)',
  },
  info: {
    backgroundColor: 'var(--color-bg-elevated)',
    color: 'var(--color-text-muted)',
    borderColor: 'var(--color-border)',
  },
};

function epiChipStyle(status: string | null): React.CSSProperties {
  // status is typed string|null but the backend has shipped objects in this
  // field — coerce defensively (same React #31 class as the render guards).
  const upper = (typeof status === 'string' ? status : '').toUpperCase();
  if (upper.startsWith('SUPPORTED')) {
    return {
      backgroundColor: 'transparent',
      color: 'var(--color-verified)',
      borderColor: 'var(--color-verified)',
    };
  }
  if (upper === 'CONTESTED') {
    return {
      backgroundColor: 'var(--color-needs-human-tint)',
      color: 'var(--color-needs-human-text)',
      borderColor: 'var(--color-needs-human-tint-bd)',
    };
  }
  return {
    backgroundColor: 'var(--color-bg-elevated)',
    color: 'var(--color-text-secondary)',
    borderColor: 'var(--color-border)',
  };
}

function SeatBadge({ seat }: { seat: string }) {
  return (
    <span
      className="cs-chip inline-flex items-center px-2 py-0.5 rounded-full border"
      style={{
        backgroundColor: 'var(--color-bg-elevated)',
        color: 'var(--color-text-secondary)',
        borderColor: 'var(--color-border)',
      }}
    >
      {getAgentDisplayName(seat)}
    </span>
  );
}

function safeSub(v: React.ReactNode): React.ReactNode {
  if (v == null || v === '' || v === false) return null;
  if (typeof v === 'string') return safeText(v);
  if (typeof v === 'number' || typeof v === 'boolean' || React.isValidElement(v)) {
    return v;
  }
  return safeText(v);
}

function StatCard({
  label,
  value,
  valueColor,
  sub,
}: {
  label: string;
  value: string;
  valueColor?: string;
  sub?: React.ReactNode;
}) {
  const subNode = safeSub(sub);
  return (
    <div
      className="rounded-lg border p-4"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
    >
      <p className="cs-label mb-1">{safeText(label)}</p>
      <p className="cs-figure-lg" style={{ color: valueColor || 'var(--color-text-primary)' }}>
        {safeText(value)}
      </p>
      {subNode != null && (
        <p className="cs-meta mt-1" style={{ color: 'var(--color-text-dim)' }}>
          {subNode}
        </p>
      )}
    </div>
  );
}

/**
 * Carrier emphasis (the "headline flip"). Renders ONLY when the server supplied
 * `brief.carrier_emphasis` — i.e. the firm's client_side === "carrier". The carrier
 * floor leads (brand); the plaintiff net is the opposing position. Figures are read
 * straight from the block (server reads them from carrier_position / mediator_view —
 * nothing is recomputed by side). Absent on every plaintiff brief, so plaintiff
 * renders are byte-identical.
 */
export function CarrierEmphasisBanner({ data }: { data: CarrierEmphasis }) {
  return (
    <div
      className="rounded-lg border p-5"
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        borderColor: 'var(--color-border)',
        borderLeft: '3px solid var(--color-brand)',
      }}
      data-testid="carrier-emphasis"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="cs-label" style={{ color: 'var(--color-needs-human-text)' }}>
          Carrier position
        </p>
        {data.lead.attestable && (
          <span
            className="cs-chip inline-flex items-center px-2 py-0.5 rounded-full border"
            style={{ color: 'var(--color-verified)', borderColor: 'var(--color-verified)' }}
          >
            Engine-grounded{data.lead.default_mitigation ? ' · default mitigation' : ''}
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="cs-label mb-1">{safeText(data.lead.label)}</p>
          <p className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}>
            {data.lead.value != null ? formatCurrency(data.lead.value) : '—'}
          </p>
        </div>
        <div>
          <p className="cs-label mb-1">{safeText(data.opposing.label)}</p>
          <p className="cs-figure-lg" style={{ color: 'var(--color-text-secondary)' }}>
            {data.opposing.value != null ? formatCurrency(data.opposing.value) : '—'}
          </p>
        </div>
      </div>
      {safeText(data.note) && (
        <p className="cs-meta mt-3" style={{ color: 'var(--color-text-dim)' }}>
          {safeText(data.note)}
        </p>
      )}
    </div>
  );
}

function SectionHeading({
  icon,
  title,
  count,
}: {
  icon: React.ReactNode;
  title: string;
  count?: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="cs-h3">{title}</h3>
      {count !== undefined && (
        <span className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>
          ({count})
        </span>
      )}
    </div>
  );
}

const attackTypeLabels: Record<string, string> = {
  methodology: 'Methodology',
  period_of_restoration: 'Period of Restoration',
  catch_up_sales: 'Catch-Up Sales',
  non_covered_cause: 'Non-Covered Cause',
};

function attackTypeLabel(t: string): string {
  return attackTypeLabels[t] || t;
}

function collectionStatusChipStyle(state: string): React.CSSProperties {
  if (state === 'requested') {
    return {
      backgroundColor: 'transparent',
      color: 'var(--color-verified)',
      borderColor: 'var(--color-verified)',
    };
  }
  if (state === 'not_requested') {
    return {
      backgroundColor: 'var(--color-needs-human-tint)',
      color: 'var(--color-needs-human-text)',
      borderColor: 'var(--color-needs-human-tint-bd)',
    };
  }
  return {
    backgroundColor: 'var(--color-bg-elevated)',
    color: 'var(--color-text-secondary)',
    borderColor: 'var(--color-border)',
  };
}

function collectionStatusLabel(status: BriefCollectionStatus): string {
  if (status.state === 'requested') {
    return status.requested_at
      ? `Requested ${new Date(status.requested_at).toLocaleDateString()}`
      : 'Requested';
  }
  if (status.state === 'not_requested') return 'Not requested';
  return 'Unknown';
}

function DocumentChip({ doc }: { doc: BriefDocumentRef }) {
  return (
    <span
      className="cs-chip inline-flex items-center px-2 py-0.5 rounded-full border"
      style={{
        backgroundColor: 'var(--color-bg-elevated)',
        color: 'var(--color-text-secondary)',
        borderColor: 'var(--color-border)',
      }}
    >
      {safeText(doc.display_name)}
    </span>
  );
}

/** One counter-strategy card — the attorney's working view of a single carrier
 * attack: their argument, our prepared counter (or a loud "gap" callout when
 * none exists), the evidence still needed, and what it buys us if collected. */
function CounterStrategyCard({ entry }: { entry: BriefCounterStrategy }) {
  const status = entry.collection_status;
  return (
    <div className="border rounded-lg p-4" style={{ borderColor: 'var(--color-border-sub)' }}>
      <p className="cs-label mb-1" style={{ color: 'var(--color-text-dim)' }}>
        {safeText(attackTypeLabel(entry.attack_type)) || '—'}
      </p>
      <p className="cs-body font-semibold" style={{ color: 'var(--color-text-primary)', fontSize: '0.9375rem' }}>
        {safeText(entry.defense_argument)}
      </p>

      <div className="mt-3">
        {entry.counter_gap ? (
          <span
            className="cs-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border"
            style={{
              backgroundColor: 'var(--color-status-adverse)',
              color: 'var(--color-on-brand)',
              borderColor: 'var(--color-status-adverse)',
            }}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            No prepared counter — gap
          </span>
        ) : (
          <div>
            <p className="cs-label" style={{ color: 'var(--color-text-dim)' }}>
              Our Counter
            </p>
            <p className="cs-body">{safeText(entry.our_counter) || '—'}</p>
          </div>
        )}
      </div>

      {entry.evidence_needed.length > 0 && (
        <div className="mt-3">
          <p className="cs-label mb-1.5" style={{ color: 'var(--color-text-dim)' }}>
            Evidence Needed
          </p>
          <div className="flex flex-wrap gap-2">
            {entry.evidence_needed.map((doc) => (
              <span key={doc.requirement_key} className="inline-flex items-center gap-1">
                <DocumentChip doc={doc} />
                <span
                  className="cs-chip inline-flex items-center px-2 py-0.5 rounded-full border"
                  style={collectionStatusChipStyle(status.state)}
                >
                  {collectionStatusLabel(status)}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {safeText(entry.impact_if_collected) && (
        <p className="cs-meta mt-3" style={{ color: 'var(--color-text-muted)' }}>
          If collected: {safeText(entry.impact_if_collected)}
        </p>
      )}

      {entry.carrier_will_pull.length > 0 && (
        <details className="mt-2">
          <summary className="cs-meta cursor-pointer" style={{ color: 'var(--color-text-ghost)' }}>
            Carrier will pull ({entry.carrier_will_pull.length})
          </summary>
          <ul className="cs-meta mt-1 pl-4 list-disc space-y-0.5" style={{ color: 'var(--color-text-ghost)' }}>
            {entry.carrier_will_pull.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export function BriefTab({ reportId }: BriefTabProps) {
  const [brief, setBrief] = useState<Brief | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noBrief, setNoBrief] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [requestingDocs, setRequestingDocs] = useState(false);
  const [requestedDocs, setRequestedDocs] = useState(false);
  const [requestDocsError, setRequestDocsError] = useState<string | null>(null);
  // Mitigation-ladder attestation state (owner ruling 2026-09-21). concededKeys drives
  // the per-rung "Conceded" badge (interpretation p>=1); citeByFactor stages the single
  // top candidate cite the accountant CREDIT/CONCEDE buttons fire from (never fabricated
  // — a factor with no staged candidate leaves its buttons disabled).
  const [concededKeys, setConcededKeys] = useState<string[]>([]);
  const [citeByFactor, setCiteByFactor] = useState<Record<string, { doc_id: string; quote: string }>>({});
  const role = useWorkspaceRole();
  const canAttest = role === 'accountant_internal' || role === 'accountant_external' || role === 'admin';

  // Load the interpretation p_proven (conceded set) + staged candidate cites. Best-effort
  // enrichment: any failure leaves the ladder read-only, never blocks the brief.
  const loadAttestState = useCallback(async () => {
    try {
      const [interp, cands] = await Promise.all([
        getInterpretation(reportId).catch(() => null),
        getInterpretationCandidates(reportId).catch(() => null),
      ]);
      const conceded = Object.entries(interp?.p_proven ?? {})
        .filter(([, v]) => ((v as { p?: number })?.p ?? 0) >= 1)
        .map(([k]) => k);
      setConcededKeys(conceded);
      const cites: Record<string, { doc_id: string; quote: string }> = {};
      cands?.by_factor?.forEach((row) => {
        const top = row.candidates?.[0];
        if (top?.doc_id && top?.quote) cites[row.factor_id] = { doc_id: top.doc_id, quote: top.quote };
      });
      setCiteByFactor(cites);
    } catch {
      /* enrichment only — leave the ladder read-only on failure */
    }
  }, [reportId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoBrief(false);
    try {
      const res = await getBrief(reportId);
      setBrief(res.brief);
      void loadAttestState();
    } catch (err: any) {
      const message: string = err?.message || '';
      const status = err?.response?.status ?? err?.status;
      const isEmptyState =
        status === 404 ||
        message.includes('404') ||
        message.includes('not found') ||
        /no brief (found|available)/i.test(message);
      if (isEmptyState) {
        setNoBrief(true);
      } else {
        setError(message || 'Failed to load the War Room brief');
      }
    } finally {
      setLoading(false);
    }
  }, [reportId, loadAttestState]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Silent refresh after a ladder action — updates the carrier floor (Credit reruns the
  // engine pipeline) + the conceded set WITHOUT the full-page loading spinner.
  const refreshAfterAction = useCallback(async () => {
    try {
      const res = await getBrief(reportId);
      setBrief(res.brief);
    } catch {
      /* keep the prior brief on a transient refetch failure */
    }
    await loadAttestState();
  }, [reportId, loadAttestState]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadBriefMarkdown(reportId);
    } catch (err: any) {
      setDownloadError(err.message || 'Failed to download brief');
    } finally {
      setDownloading(false);
    }
  }, [reportId]);

  // ===========================================================================
  // LOADING
  // ===========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
      </div>
    );
  }

  // ===========================================================================
  // EMPTY STATE — no brief generated yet
  // ===========================================================================

  if (noBrief) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
        <h3 className="cs-h3 mb-2">No Brief Available Yet</h3>
        <p className="cs-body mb-6 max-w-md mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
          The War Room brief summarizes this deliberation into a readable one-page
          report. It becomes available once the fleet analysis has run.
        </p>
        <button
          onClick={fetchData}
          className="cs-label inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
        >
          Check Again
        </button>
      </div>
    );
  }

  // ===========================================================================
  // ERROR
  // ===========================================================================

  if (error || !brief) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--color-needs-human-text)' }} />
        <p className="cs-body mb-4" style={{ color: 'var(--color-text-secondary)' }}>
          {error || 'Failed to load the War Room brief'}
        </p>
        <button
          onClick={fetchData}
          className="cs-label px-4 py-2 rounded-lg transition-colors"
          style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ===========================================================================
  // DATA VIEW
  // ===========================================================================

  const {
    headline,
    agreements,
    disputes,
    gate_findings,
    defense_preview,
    next_actions,
    caveats,
    sections_omitted,
    counter_strategy,
    methodology_paths,
    document_request_endpoint,
    carrier_position,
    carrier_emphasis,
  } = brief;

  const sortedActions = [...(next_actions || [])].sort((a, b) => {
    const rank = (p: string) => (p === 'high' ? 0 : 1);
    return rank(a.priority) - rank(b.priority);
  });

  // Ready methodologies first, then by supportable figure descending (nulls last).
  const sortedMethodologyPaths = [...(methodology_paths || [])].sort((a, b) => {
    if (a.ready !== b.ready) return a.ready ? -1 : 1;
    const av = a.supportable_figure ?? -Infinity;
    const bv = b.supportable_figure ?? -Infinity;
    return bv - av;
  });

  const hasUnrequestedCounterEvidence = (counter_strategy || []).some(
    (c) => c.evidence_needed.length > 0 && c.collection_status.state === 'not_requested',
  );
  const hasUnrequestedMethodologyDocs = (methodology_paths || []).some(
    (m) => m.missing_documents.length > 0,
  );
  const showRequestDocumentsButton =
    !!document_request_endpoint && (hasUnrequestedCounterEvidence || hasUnrequestedMethodologyDocs);

  const handleRequestDocuments = async () => {
    if (!document_request_endpoint) return;
    setRequestingDocs(true);
    setRequestDocsError(null);
    try {
      const methodIds = (methodology_paths || [])
        .filter((m) => m.missing_documents.length > 0)
        .map((m) => m.method_id);
      await requestBriefDocuments(document_request_endpoint, methodIds.length > 0 ? methodIds : undefined);
      setRequestedDocs(true);
    } catch (err: any) {
      setRequestDocsError(err?.message || 'Failed to request documents');
    } finally {
      setRequestingDocs(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="brief-tab">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" style={{ color: 'var(--color-needs-human-text)' }} />
            <h2 className="cs-h2">War Room Brief</h2>
          </div>
          <p className="cs-meta mt-1">
            {brief.run_id ? `Run ${brief.run_id} · ` : ''}
            Generated {new Date(brief.generated_at).toLocaleString()}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="cs-label inline-flex items-center gap-2 px-3 py-2 rounded-lg disabled:opacity-50 transition-colors border"
            style={{
              color: 'var(--color-text-secondary)',
              backgroundColor: 'var(--color-bg-surface)',
              borderColor: 'var(--color-border)',
            }}
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download .md
          </button>
          {downloadError && (
            <span className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
              {downloadError}
            </span>
          )}
        </div>
      </div>

      {/* Carrier emphasis — leads with the carrier floor when the firm is carrier-side.
          Absent on every plaintiff brief → the plaintiff render below is byte-identical. */}
      {carrier_emphasis && <CarrierEmphasisBanner data={carrier_emphasis} />}

      {/* Headline stat grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {headline.elected_bi && headline.elected_bi.value != null && (
          <StatCard
            label="BI — elected net"
            value={formatCurrency(headline.elected_bi.value)}
            sub={`${headline.elected_bi.method.replace('_', '-')} method · net of policy deductions${
              headline.elected_bi.elected_by === 'attorney' ? '' : ' · default (no election)'}`}
          />
        )}
        <StatCard
          label="Bottom-up loss sustained (gross)"
          value={formatCurrency(headline.bi_total_loss)}
          sub={
            !(headline.elected_bi && headline.elected_bi.value != null) && typeof headline.sworn_net_claim === 'number'
              ? <>Bottom-up, pre-deduction · sworn net <span className="font-mono tabular-nums">{formatCurrency(headline.sworn_net_claim)}</span></>
              : 'Bottom-up, pre-deduction'
          }
        />
        <StatCard
          label="Defensible Range"
          value={`${formatCurrency(headline.defensible_range.low)} – ${formatCurrency(headline.defensible_range.high)}`}
          sub={headline.defensible_range.methodology}
        />
        <div
          className="rounded-lg border p-4"
          style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
        >
          <p className="cs-label mb-1">EPI</p>
          <span
            className="cs-chip inline-flex items-center px-2 py-0.5 rounded-full border mb-1"
            style={epiChipStyle(headline.epi.status)}
          >
            {safeText(headline.epi.status) || 'UNKNOWN'}
          </span>
          <p className="cs-figure" style={{ color: 'var(--color-text-primary)' }}>
            <FinancialValue value={headline.epi.total_epi_loss} />
          </p>
          {safeText(headline.epi.reason) && (
            <p className="cs-meta mt-1" style={{ color: 'var(--color-text-dim)' }}>
              {safeText(headline.epi.reason)}
            </p>
          )}
        </div>
        <StatCard
          label="Recommended Recovery (P50)"
          value={formatCurrency(headline.recommended_recovery_p50)}
        />
        <StatCard
          label={headline.supportable_ceiling_label || 'BI ceiling (forensic high)'}
          value={formatCurrency(headline.supportable_ceiling)}
          valueColor="var(--color-text-secondary)"
          sub={headline.ceiling_derivation}
        />
      </div>

      {/* Where the seats agree */}
      {agreements && agreements.length > 0 && (
        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <SectionHeading
            icon={<CheckCircle2 className="h-5 w-5" style={{ color: 'var(--color-verified)' }} />}
            title="Where the Seats Agree"
            count={agreements.length}
          />
          <div className="space-y-3">
            {agreements.map((a, i) => (
              <div key={`${a.engine}-${i}`} className="flex items-start justify-between gap-3 border-b pb-3 last:border-0 last:pb-0" style={{ borderColor: 'var(--color-border-sub)' }}>
                <div className="min-w-0">
                  <p className="cs-label" style={{ color: 'var(--color-text-dim)' }}>{safeText(a.engine)}</p>
                  <p className="cs-body">{safeText(a.position) || '—'}</p>
                </div>
                <div className="flex flex-wrap gap-1 justify-end shrink-0">
                  {a.seats.map((s) => (
                    <SeatBadge key={s} seat={s} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open disputes */}
      {disputes && disputes.length > 0 && (
        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <SectionHeading
            icon={<Scale className="h-5 w-5" style={{ color: 'var(--color-brand)' }} />}
            title="Open Disputes"
            count={disputes.length}
          />
          <div className="space-y-4">
            {disputes.map((d, i) => (
              <div key={`${d.topic}-${i}`} className="border-b pb-4 last:border-0 last:pb-0" style={{ borderColor: 'var(--color-border-sub)' }}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>{safeText(d.topic)}</p>
                  {d.dollars_at_stake != null && (
                    <span className="cs-figure" style={{ color: 'var(--color-needs-human-text)' }}>
                      <FinancialValue value={d.dollars_at_stake} /> at stake
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {d.seats.map((s) => (
                    <SeatBadge key={s} seat={s} />
                  ))}
                </div>
                {safeText(d.positions) && (
                  <p className="cs-meta mt-2" style={{ color: 'var(--color-text-secondary)' }}>{safeText(d.positions)}</p>
                )}
                {safeText(d.resolution) && (
                  <p className="cs-meta mt-1" style={{ color: 'var(--color-text-dim)' }}>
                    Resolves via: {safeText(d.resolution)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Factual defects (consistency gate findings) */}
      {gate_findings && gate_findings.length > 0 && (
        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <SectionHeading
            icon={<AlertTriangle className="h-5 w-5" style={{ color: 'var(--color-status-adverse)' }} />}
            title="Factual Defects"
            count={gate_findings.length}
          />
          <div className="space-y-2">
            {gate_findings.map((g: BriefGateFinding, i) => (
              <div key={`${g.fact_key}-${i}`} className="flex items-start gap-3 p-3 border rounded-lg" style={{ borderColor: 'var(--color-border-sub)' }}>
                <span
                  className="cs-chip inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border shrink-0"
                  style={severityChipStyle[g.severity] || severityChipStyle.info}
                >
                  {safeText(g.severity)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="cs-meta font-mono" style={{ color: 'var(--color-text-dim)' }}>{safeText(g.fact_key)}</p>
                  <p className="cs-body">{safeText(g.detail)}</p>
                  {g.seats.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {g.seats.map((s) => (
                        <SeatBadge key={s} seat={s} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Defense preview — the adversary's view, visually distinct */}
      {defense_preview && (
        <div
          className="rounded-xl border-2 p-5"
          style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-status-adverse)' }}
        >
          <SectionHeading
            icon={<Swords className="h-5 w-5" style={{ color: 'var(--color-status-adverse)' }} />}
            title="Defense Preview — Carrier's Likely Position"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {safeText(defense_preview.primary_attack) && (
              <div className="sm:col-span-2">
                <p className="cs-label" style={{ color: 'var(--color-text-dim)' }}>Primary Attack</p>
                <p className="cs-body">{safeText(defense_preview.primary_attack)}</p>
              </div>
            )}
            {safeText(defense_preview.attack_vector) && (
              <div>
                <p className="cs-label" style={{ color: 'var(--color-text-dim)' }}>Attack Vector</p>
                <p className="cs-body">{safeText(defense_preview.attack_vector)}</p>
              </div>
            )}
            {safeText(defense_preview.leverage_move) && (
              <div>
                <p className="cs-label" style={{ color: 'var(--color-text-dim)' }}>Leverage Move</p>
                <p className="cs-body">{safeText(defense_preview.leverage_move)}</p>
              </div>
            )}
            {defense_preview.alternative_damages != null && (
              <div>
                <p className="cs-label" style={{ color: 'var(--color-text-dim)' }}>Alternative Damages</p>
                <p className="cs-figure" style={{ color: 'var(--color-status-adverse)' }}>
                  <FinancialValue value={defense_preview.alternative_damages} />
                </p>
              </div>
            )}
            {defense_preview.mediation_position != null && (
              <div>
                <p className="cs-label" style={{ color: 'var(--color-text-dim)' }}>Mediation Position</p>
                <p className="cs-figure" style={{ color: 'var(--color-status-adverse)' }}>
                  <FinancialValue value={defense_preview.mediation_position} />
                </p>
              </div>
            )}
            {defense_preview.daubert_score != null && (
              <div>
                <p className="cs-label" style={{ color: 'var(--color-text-dim)' }}>Daubert Score</p>
                <p className="cs-figure" style={{ color: 'var(--color-text-primary)' }}>
                  {safeText(defense_preview.daubert_score)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Carrier Position — the ENGINE-GROUNDED counterpart to the defense preview's
          raw LLM floats above: the deterministic carrier floor composed from certified
          engine outputs, with the carrier's asserted (advocacy) reductions quarantined
          and never applied. Doubles as the plaintiff's "why is their number low"
          counter-exhibit. Renders only when the backend supplied carrier_position. */}
      {carrier_position && (
        <CarrierPositionLedger
          data={carrier_position}
          attest={{
            reportId,
            canAttest,
            concededKeys,
            citeByFactor,
            onChanged: refreshAfterAction,
          } satisfies LadderAttest}
        />
      )}

      {/* Counter-Strategy — the attorney's working view: carrier attack vs our prepared counter */}
      {counter_strategy && counter_strategy.length > 0 && (
        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <SectionHeading
            icon={<ShieldAlert className="h-5 w-5" style={{ color: 'var(--color-status-adverse)' }} />}
            title="Counter-Strategy"
            count={counter_strategy.length}
          />
          <div className="space-y-4">
            {counter_strategy.map((c, i) => (
              <CounterStrategyCard key={`${c.attack_type}-${i}`} entry={c} />
            ))}
          </div>
        </div>
      )}

      {/* Methodology Paths */}
      {methodology_paths && methodology_paths.length > 0 && (
        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <SectionHeading
            icon={<Layers className="h-5 w-5" style={{ color: 'var(--color-needs-human-text)' }} />}
            title="Methodology Paths"
            count={methodology_paths.length}
          />
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="methodology-paths-table">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                  <th className="cs-label text-left py-2 pr-3">Methodology</th>
                  <th className="cs-label text-right py-2 px-3">Supportable</th>
                  <th className="cs-label text-left py-2 px-3">Status</th>
                  <th className="cs-label text-left py-2 px-3">Missing Documents</th>
                  <th className="cs-label text-left py-2 pl-3">Readiness</th>
                </tr>
              </thead>
              <tbody>
                {sortedMethodologyPaths.map((m: BriefMethodologyPath) => (
                  <tr key={m.method_id} style={{ borderBottom: '1px solid var(--color-border-faint)' }}>
                    <td className="cs-body py-2.5 pr-3" style={{ color: 'var(--color-text-primary)' }}>
                      {safeText(m.label)}
                    </td>
                    <td className="cs-figure py-2.5 px-3 text-right">
                      <FinancialValue value={m.supportable_figure} />
                    </td>
                    <td className="cs-meta py-2.5 px-3">{safeText(m.status) || '—'}</td>
                    <td className="py-2.5 px-3">
                      {m.missing_documents.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {m.missing_documents.map((doc) => (
                            <DocumentChip key={doc.requirement_key} doc={doc} />
                          ))}
                        </div>
                      ) : (
                        <span className="cs-meta">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pl-3">
                      {m.ready ? (
                        <span
                          className="cs-chip inline-flex items-center px-2 py-0.5 rounded-full border"
                          style={{
                            backgroundColor: 'transparent',
                            color: 'var(--color-verified)',
                            borderColor: 'var(--color-verified)',
                          }}
                        >
                          READY
                        </span>
                      ) : (
                        <span className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
                          {m.missing_documents.length} blocked
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Request missing documents — only when the brief supplies a live endpoint
          and there's actually something outstanding to request. */}
      {showRequestDocumentsButton && (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleRequestDocuments}
            disabled={requestingDocs || requestedDocs}
            className="cs-label inline-flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
            style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
          >
            {requestingDocs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Request Missing Documents
          </button>
          {requestedDocs && (
            <span className="cs-meta inline-flex items-center gap-1" style={{ color: 'var(--color-verified)' }}>
              <CheckCircle2 className="h-4 w-4" />
              Requested
            </span>
          )}
          {requestDocsError && (
            <span className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
              {requestDocsError}
            </span>
          )}
        </div>
      )}

      {/* Next actions */}
      {sortedActions.length > 0 && (
        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <SectionHeading
            icon={<ListChecks className="h-5 w-5" style={{ color: 'var(--color-needs-human-text)' }} />}
            title="Next Actions"
            count={sortedActions.length}
          />
          <div className="space-y-2">
            {sortedActions.map((a, i) => (
              <div key={`${a.action}-${i}`} className="flex items-start gap-3 p-3 border rounded-lg" style={{ borderColor: 'var(--color-border-sub)' }}>
                {a.priority === 'high' && (
                  <span
                    className="cs-chip inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border shrink-0"
                    style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' }}
                  >
                    high
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="cs-body">{safeText(a.action)}</p>
                  {a.source_seat && (
                    <p className="cs-meta mt-0.5" style={{ color: 'var(--color-text-dim)' }}>
                      {getAgentDisplayName(a.source_seat)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Caveats — subdued callout */}
      {caveats && caveats.length > 0 && (
        <div
          className="rounded-lg border px-4 py-3 flex gap-3 items-start"
          style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
        >
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
          <ul className="cs-meta space-y-1" style={{ color: 'var(--color-text-muted)' }}>
            {caveats.map((c, i) => (
              <li key={i}>{safeText(c)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Sections omitted — fine print */}
      {sections_omitted && sections_omitted.length > 0 && (
        <p className="cs-meta text-center" style={{ color: 'var(--color-text-ghost)' }}>
          Sections omitted (no data): {sections_omitted.join(', ')}
        </p>
      )}
    </div>
  );
}

export default BriefTab;
